import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import { Plus, User, Building, Trash2 } from 'lucide-react'
import type { NomineeDirectorProtectedPerson } from '@/types'

type Props = {
  protected_persons: NomineeDirectorProtectedPerson[]
  nominee_director: { id: string; type_name: string }
}

export default function ProtectedPersonsIndex({ protected_persons, nominee_director }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()

  return (
    <DashboardLayout
      title="Protected Persons"
      breadcrumbs={[
        { label: 'Nominee Directors', href: 'nominee_directors' },
        { label: nominee_director.type_name, href: `nominee_directors/${nominee_director.id}` },
        { label: 'Protected Persons' },
      ]}
    >
      <div>
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Protected Persons</h1>
              <p className="text-sm text-muted-foreground">
                People the nominee director will represent
              </p>
            </div>
            <Button asChild size="sm">
              <Link href={`${basePath}/nominee_directors/${nominee_director.id}/protected_persons/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add Person
              </Link>
            </Button>
          </div>
        </div>

        {protected_persons.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <User className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No protected persons added yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add the people who the nominee director will stand in for
            </p>
            <Button asChild size="sm">
              <Link href={`${basePath}/nominee_directors/${nominee_director.id}/protected_persons/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add First Person
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-0.5">
            {protected_persons.map((person) => (
              <div key={person.id} className="group flex items-center justify-between py-2 px-3 -mx-3 rounded-lg hover:bg-muted transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center shrink-0">
                    {person.person_kind === 'entity' ? (
                      <Building className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <User className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{person.full_name}</div>
                    {person.legal_name && (
                      <div className="text-xs text-muted-foreground">{person.legal_name}</div>
                    )}
                    {person.email && (
                      <div className="text-xs text-muted-foreground">{person.email}</div>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {person.city && person.state_province
                          ? `${person.city}, ${person.state_province}`
                          : person.country_code}
                      </span>
                      <Badge variant="outline" className="text-xs py-0">
                        {person.person_kind}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={async () => {
                      if (await confirm({ description: 'Remove this person?', confirmLabel: 'Remove', destructive: true })) {
                        router.delete(`${basePath}/nominee_directors/${nominee_director.id}/protected_persons/${person.id}`)
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
